export type SessionUploadState =
  | "idle"
  | "uploading"
  | "uploaded"
  | "retrying"
  | "blocked"

const ACCEPTED_MIME_TYPES = [
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
  "image/avif",
  "video/quicktime",
  "video/mp4",
] as const

const ACCEPTED_EXTENSIONS = ["heic", "heif", "avif", "mov", "mp4"] as const

const acceptedMimeTypeSet = new Set<string>(ACCEPTED_MIME_TYPES)
const acceptedExtensionSet = new Set<string>(ACCEPTED_EXTENSIONS)

export const VIRTUAL_TRY_ON_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024
export const MAX_SESSION_UPLOAD_ATTEMPTS = 10
export const VIRTUAL_TRY_ON_FILE_ACCEPT_ATTRIBUTE =
  ".heic,.heif,.avif,image/heic,image/heif,.mov,.mp4"

type FileLike = {
  name?: string | null
  type?: string | null
  size?: number | null
}

const normalize = (value: unknown): string =>
  typeof value === "string" ? value.trim().toLowerCase() : ""

const getExtension = (fileName: unknown): string => {
  const normalized = normalize(fileName)
  if (!normalized) {
    return ""
  }
  const dotIndex = normalized.lastIndexOf(".")
  if (dotIndex === -1 || dotIndex === normalized.length - 1) {
    return ""
  }
  return normalized.slice(dotIndex + 1)
}

export const isVirtualTryOnMimeTypeAccepted = (mimeType: unknown): boolean => {
  const normalized = normalize(mimeType)
  if (!normalized) {
    return false
  }

  return (
    normalized.startsWith("image/") ||
    acceptedMimeTypeSet.has(normalized) ||
    normalized.startsWith("video/")
  )
}

export const isVirtualTryOnExtensionAccepted = (fileName: unknown): boolean => {
  return acceptedExtensionSet.has(getExtension(fileName))
}

export const isVirtualTryOnFileAccepted = (file: FileLike): boolean => {
  return (
    isVirtualTryOnMimeTypeAccepted(file.type) ||
    isVirtualTryOnExtensionAccepted(file.name)
  )
}

export const validateVirtualTryOnImageFile = (
  file: FileLike
): { ok: true } | { ok: false; error: string } => {
  if (!isVirtualTryOnFileAccepted(file)) {
    return {
      ok: false,
      error: "Este formato de imagen no es compatible.",
    }
  }

  const size = typeof file.size === "number" ? file.size : 0
  if (size > VIRTUAL_TRY_ON_MAX_FILE_SIZE_BYTES) {
    return {
      ok: false,
      error: "La imagen es muy pesada. Probá con un archivo menor a 25MB.",
    }
  }

  return { ok: true }
}

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false
  }
  return (error as { name?: string }).name === "AbortError"
}

export type SessionUploadResult = "uploaded" | "blocked" | "aborted"

type SessionUploadRunnerOptions = {
  maxAttempts?: number
  onStateChange?: (state: SessionUploadState) => void
  retryDelayMs?: (attempt: number) => number
  shouldContinue?: () => boolean
  uploadOnce: (attempt: number) => Promise<void>
  wait?: (ms: number) => Promise<void>
}

const defaultRetryDelayMs = (attempt: number) => attempt * 350
const defaultWait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

export const runSessionPhotoUploadWithRetry = async (
  options: SessionUploadRunnerOptions
): Promise<SessionUploadResult> => {
  const maxAttempts = Number.isFinite(options.maxAttempts)
    ? Math.max(1, Math.floor(Number(options.maxAttempts)))
    : MAX_SESSION_UPLOAD_ATTEMPTS
  const shouldContinue = options.shouldContinue ?? (() => true)
  const wait = options.wait ?? defaultWait
  const retryDelayMs = options.retryDelayMs ?? defaultRetryDelayMs

  if (!shouldContinue()) {
    return "aborted"
  }

  options.onStateChange?.("uploading")
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (!shouldContinue()) {
      return "aborted"
    }

    try {
      await options.uploadOnce(attempt)
      if (!shouldContinue()) {
        return "aborted"
      }
      options.onStateChange?.("uploaded")
      return "uploaded"
    } catch (error) {
      if (isAbortError(error) || !shouldContinue()) {
        return "aborted"
      }

      if (attempt >= maxAttempts) {
        options.onStateChange?.("blocked")
        return "blocked"
      }

      options.onStateChange?.("retrying")
      const delay = Math.max(0, Math.floor(retryDelayMs(attempt)))
      if (delay > 0) {
        await wait(delay)
      }
      if (!shouldContinue()) {
        return "aborted"
      }
      options.onStateChange?.("uploading")
    }
  }

  options.onStateChange?.("blocked")
  return "blocked"
}
